import { createElement, useCallback, useMemo } from 'react'
import { useMutation, useQuery } from '@apollo/client'
import { growl } from '@wmgtech/legato'

import { IMediaBuyer } from 'modules/Admin'
import { CREATE_MEDIA_BUYER, DELETE_MEDIA_BUYER, MEDIA_BUYER_LIST, UPDATE_MEDIA_BUYER } from '../constants'
import { Toast } from 'components/Toast'

interface IMediaBuyerInput {
  name: string
}

interface IMediaBuyerUpdateInput {
  id: string
  name: string
}

export const useMediaBuyer = () => {
  const { data: mediaBuyers, loading } = useQuery<{ getMediaBuyers: [IMediaBuyer] }>(MEDIA_BUYER_LIST, {
    fetchPolicy: 'cache-and-network',
  })

  const [createMediaBuyerMutation] = useMutation<{ createMediaBuyer: IMediaBuyer }, { input: IMediaBuyerInput }>(CREATE_MEDIA_BUYER,{
    update(cache) {
      cache.modify({
        fields: {
          getMediaBuyers() {},
        },
      })
    },
  })

  const [updateMediaBuyerMutation] = useMutation<{ updateAdvertiser: IMediaBuyer }, { input: IMediaBuyerUpdateInput }>(UPDATE_MEDIA_BUYER,{
    update(cache) {
      cache.modify({
        fields: {
          getMediaBuyers() {},
        },
      })
    },
  })

  const [deleteMediaBuyerMutation] = useMutation<{ deleteMediaBuyer: { data: any } }>(DELETE_MEDIA_BUYER, {
    update(cache) {
      cache.modify({
        fields: {
          getMediaBuyers() {},
        },
      })
    },
  })

  const mediaBuyerByName = useMemo(() => mediaBuyers?.getMediaBuyers?.map((buyer: IMediaBuyer) =>
      buyer.name.toLowerCase()) ?? [],
    [mediaBuyers?.getMediaBuyers])

  const showError = useCallback((name : string) => {
    growl.error({
      message: createElement(Toast, { message: `${name} is already exist` }),
      containerStyle: 'tint',
      colorType: 'danger',
      icon: 'check',
    }, { autoClose: 3000 })
  }, [])

  const createMediaBuyer = useCallback(
    async ( name : string) => {
      if (mediaBuyerByName.indexOf(name.toLowerCase()) > -1) {
        return showError(name)
      }
      await createMediaBuyerMutation({
        variables: {
          input: { name },
        },
      })
      growl.success({
        message: createElement(Toast, { message: `${name} has been created successfully` }),
        containerStyle: 'tint',
        colorType: 'success',
        icon: 'check',
      }, { autoClose: 3000 })
    },
    [createMediaBuyerMutation, mediaBuyerByName, showError],
  )

  const updateMediaBuyer = useCallback(
    async (id: string, name: string) => {
      if (mediaBuyerByName.indexOf(name.toLowerCase()) > -1) {
        return showError(name)
      }
      await updateMediaBuyerMutation({
        variables: {
          input: { id, name },
        },
      })
      growl.success({
        message: createElement(Toast, { message: `${name} has been saved successfully` }),
        containerStyle: 'tint',
        colorType: 'success',
        icon: 'check',
      }, { autoClose: 3000 })
    },
    [updateMediaBuyerMutation, mediaBuyerByName, showError],
  )

  const deleteMediaBuyer = useCallback<(id: string) => void>(
    async (id) => {
      await deleteMediaBuyerMutation({ variables: { id } })

      if (mediaBuyers?.getMediaBuyers) {
        const buyer = mediaBuyers?.getMediaBuyers.find((adv) => adv.id === id)
        if (buyer) {
          growl.success({
            message: createElement(Toast, { message: `Media Buyer ${buyer.name} has been successfully deleted` }),
            containerStyle: 'tint',
            colorType: 'success',
            icon: 'check',
          }, { autoClose: 3000 })
        }
      }

    },
    [deleteMediaBuyerMutation, mediaBuyers],
  )

  const mediaBuyersDta = useMemo(() => mediaBuyers?.getMediaBuyers ?
    [...mediaBuyers.getMediaBuyers].sort((a, b) => a.name.localeCompare(b.name)):
    mediaBuyers?.getMediaBuyers, [mediaBuyers?.getMediaBuyers])

  return {
    mediaBuyers: mediaBuyersDta,
    loading,
    createMediaBuyer,
    updateMediaBuyer,
    deleteMediaBuyer,
  }
}
